import React from "react";
import SearchSelect from "./SearchSelect";

/**
 * Reusable page-level filter bar built on SearchSelect.
 *
 * Each filter in `filters` is:
 *   {
 *     key:       unique string (required)
 *     value:     current filter value as a string ("" = no filter)
 *     onChange:  (nextValue: string) => void
 *     allLabel:  placeholder / "all" text (e.g. "All Types")
 *     options:   array of strings OR { label, value } objects
 *     searchable: optional bool (default: true)
 *     minWidth:  optional css width (default: "180px")
 *   }
 *
 * Filter state stays as plain strings in the parent; this component handles
 * mapping strings <-> SearchSelect's { label, value } option objects.
 */
export default function FilterBar({
  filters = [],
  onClear,
  showClear = false,
  className = "",
}) {
  // Normalize a raw option (string or object) into { label, value }
  const normalizeOption = (opt) => {
    if (typeof opt === "string") {
      return {
        value: opt,
        label: opt.charAt(0).toUpperCase() + opt.slice(1),
      };
    }
    return opt;
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {filters.map((filter) => {
        const normalizedOptions = (filter.options || []).map(normalizeOption);

        // Map the stored string value back to its option object for display
        const selectedOption =
          normalizedOptions.find((o) => o.value === filter.value) || null;

        return (
          <div
            key={filter.key}
            style={{ minWidth: filter.minWidth || "180px" }}
          >
            <SearchSelect
              options={normalizedOptions}
              value={selectedOption}
              onChange={(option) => filter.onChange?.(option?.value || "")}
              placeholder={filter.allLabel || "All"}
              searchable={filter.searchable !== false}
              isClearable
            />
          </div>
        );
      })}

      {showClear && (
        <button
          onClick={onClear}
          className="gray-button text-sm py-1.5 whitespace-nowrap"
        >
          Clear Filters
        </button>
      )}
    </div>
  );
}
